import {
  abbreviateId,
  HelpIcon,
  HStack,
  IndicatorBadge,
  type IndicatorBadgeProps,
  InlineFormattedNumber,
  NumberVariants,
  type Quote,
  SmartSummary,
  Text,
  toBigWithDefault,
  Tooltip,
  VStack,
} from '@talos/kyoko';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { Divider } from '@talos/kyoko';
import type { RFQSweeperQuote } from '../RFQSweeper';

interface RFQSweepingPanelHeaderProps {
  representativeQuote: Quote;
  sweeperQuotes: RFQSweeperQuote[];
  /** Side-aware quantity still open across tradable, non-executed markets. */
  availableQty: string;
  /** True when the remaining RFQ quantity is larger than availableQty. */
  showAvailableQtyWarning: boolean;
  suffix?: ReactNode;
}

export const RFQSweepingPanelHeader = ({
  representativeQuote,
  sweeperQuotes,
  availableQty,
  showAvailableQtyWarning,
  suffix,
}: RFQSweepingPanelHeaderProps) => {
  const currency = representativeQuote.Currency ?? 'Units';

  const tradedQty = useMemo(
    () =>
      sweeperQuotes
        .reduce((total, { quote }) => total.plus(toBigWithDefault(quote.TradedQty, 0)), toBigWithDefault(0, 0))
        .toFixed(),
    [sweeperQuotes]
  );

  return (
    <HStack justifyContent="space-between" alignItems="center" px="spacing16" py="spacing12">
      <HStack gap="spacing12">
        <HeaderItem
          label="Sweeping ID"
          testid="sweeping-panel-header-correlation-id"
          content={
            <Tooltip tooltip={representativeQuote.SweepGroupID} showUnderline>
              <Text color="colorTextImportant">{abbreviateId(representativeQuote.SweepGroupID ?? '-')}</Text>
            </Tooltip>
          }
        />
        <Divider orientation="vertical" />
        <HeaderItem
          label="RFQ Details"
          testid="sweeping-panel-header-rfq-details"
          content={<SmartSummary entity={representativeQuote} type="quote" />}
        />
      </HStack>

      <HStack gap="spacing12" alignItems="center">
        <HeaderItem
          label="Filled Qty"
          testid="sweeping-panel-header-filled-quantity"
          content={
            <HStack gap="spacing4">
              <InlineFormattedNumber number={tradedQty} currency={currency} showCurrency={false} />
              <Text color="colorTextImportant">/</Text>
              <InlineFormattedNumber number={representativeQuote.OrderQty} currency={currency} />
            </HStack>
          }
        />
        <Divider orientation="vertical" />
        <HeaderItem
          label={
            <HStack gap="spacing4">
              Available Qty
              <HelpIcon
                size={12}
                tooltip="Quantity currently available to trade across markets. Shows as yellow if there is insufficient available quantity."
              />
            </HStack>
          }
          testid="sweeping-panel-header-available-quantity"
          content={
            <InlineFormattedNumber
              number={availableQty}
              currency={currency}
              variant={showAvailableQtyWarning ? NumberVariants.Warning : undefined}
            />
          }
        />
        {suffix && (
          <>
            <Divider orientation="vertical" />
            {suffix}
          </>
        )}
      </HStack>
    </HStack>
  );
};

interface HeaderItemProps {
  label: ReactNode;
  content: ReactNode;
  variant?: IndicatorBadgeProps['variant'];
  testid?: string;
}

function HeaderItem({ label, content, variant, testid }: HeaderItemProps) {
  return (
    <VStack gap="spacing4" alignItems="flex-start" data-testid={testid} color="colorTextImportant">
      <IndicatorBadge children={label} variant={variant} />
      {content}
    </VStack>
  );
}
